import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, Button, Container, Row, Col, Card, Spinner, Alert, Table, Badge } from 'react-bootstrap';
import { getCliente, updateCliente, createCliente, getCapInfo, getListeDesiderate, createListaDesiderata, deleteListaDesiderata } from '../services/api';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';
import { debounce } from 'lodash';
import { useMediaQuery } from 'react-responsive';
import * as XLSX from 'xlsx';
import StoricoRimborsiCliente from '../common/StoricoRimborsiCliente';

const titleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const ClientiEdit = () => {
  // Mobile responsive hook
  const isMobile = useMediaQuery({ maxWidth: 991 });
  
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Aggiungi questa riga
  const [formData, setFormData] = useState({
    nome: '', cognome: '', denominazione_sociale: '', presso: '', luogo_nascita: '', data_nascita: '',
    codice_fiscale: '', partita_iva: '', indirizzo: '', numero_civico: '', cap: '',
    citta: '', provincia: '', nazione: 'ITALIA', email: '', pec: '',
    telefono: '', cellulare: '', codice_destinatario: '', punti_fedelta: 0,
    sconto_percentuale: 0.0,  // Sconto percentuale automatico
    origine: '', // New field: Origine
    contrassegno_bloccato: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tornaIndietro = useTornaIndietro('/clienti');
  const handleCancel = () => {
    if (location.state && location.state.returnTo) navigate(location.state.returnTo);
    else tornaIndietro();
  };

  // --- Liste Desiderate ---
  const [liste, setListe] = useState([]);
  const [listeLoading, setListeLoading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null); // { nomeFile, items: [{ean, titolo_cliente, qt_desiderata}] }
  const [uploadSaving, setUploadSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchClientData = async () => {
      if (id && id !== 'new') { // Se c'è un ID numerico, siamo in modalità modifica
        try {
          const response = await getCliente(id);
          const clientData = response.data;
          // Format data_nascita for date input
          if (clientData.data_nascita) {
            clientData.data_nascita = new Date(clientData.data_nascita).toISOString().split('T')[0];
          }
          setFormData({
            ...clientData,
            numero_civico: clientData.numero_civico || '', // Populate numero_civico
            origine: clientData.origine || '', // Ensure origine is not null
          });
        } catch (err) {
          setError('Errore nel caricamento dei dati del cliente.');
          console.error(err);
        } finally {
          setLoading(false);
        }
      } else { // Siamo in modalità creazione (id è 'new' o non definito)
        setFormData({
          nome: '', cognome: '', denominazione_sociale: '', presso: '', luogo_nascita: '', data_nascita: '',
          codice_fiscale: '', partita_iva: '', indirizzo: '', cap: '',
          citta: '', provincia: '', nazione: 'ITALIA', email: '', pec: '',
          telefono: '', cellulare: '', codice_destinatario: '', punti_fedelta: 0,
          origine: '',
        });
        setLoading(false);
      }
    };

    fetchClientData();
  }, [id]);

  const debouncedFetchCap = useMemo(
    () => debounce(async (cap) => {
      try {
        const response = await getCapInfo(cap);
        const { comune, provincia } = response.data;
        if (comune && provincia) {
          setFormData(prevData => {
            // Aggiorna solo se i campi sono vuoti per non sovrascrivere l'input manuale
            const newCitta = prevData.citta ? prevData.citta : comune;
            const newProvincia = prevData.provincia ? prevData.provincia : provincia;
            return { ...prevData, citta: newCitta, provincia: newProvincia };
          });
        }
      } catch (err) {
        console.error("Errore nel recupero delle info per il CAP:", err);
      }
    }, 500),
    [setFormData] // setFormData è una dipendenza stabile fornita da React, ma va inclusa
  );

  useEffect(() => {
    // Fetch CAP solo per CAP italiani (5 cifre numeriche)
    if (formData.cap && formData.cap.length === 5 && formData.cap.match(/^\d{5}$/)) {
      debouncedFetchCap(formData.cap);
    }
  }, [formData.cap, debouncedFetchCap]);

  // Carica liste al mount (solo in edit)
  useEffect(() => {
    if (!id || id === 'new') return;
    setListeLoading(true);
    getListeDesiderate(id)
      .then(r => setListe(r.data))
      .catch(() => {})
      .finally(() => setListeLoading(false));
  }, [id]);

  const handleXlsxUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const wb = XLSX.read(ev.target.result, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      if (!rows.length) { alert('Il file è vuoto o non leggibile.'); return; }

      // Cerca le colonne per EAN, Titolo, Qt (case-insensitive, partial match)
      const keys = Object.keys(rows[0]);
      const findCol = (...candidates) =>
        keys.find(k => candidates.some(c => k.toLowerCase().includes(c))) || null;

      const eanCol = findCol('ean', 'barcode', 'codice');
      const titoloCol = findCol('titolo', 'title', 'nome');
      const qtCol = findCol('qt', 'quant', 'copie', 'qta');

      const items = rows
        .map(row => ({
          ean: eanCol ? String(row[eanCol]).trim() : '',
          titolo_cliente: titoloCol ? String(row[titoloCol]).trim() : '',
          qt_desiderata: qtCol ? (parseInt(row[qtCol], 10) || 1) : 1,
        }))
        .filter(r => r.ean || r.titolo_cliente); // scarta righe vuote

      setUploadPreview({ nomeFile: file.name, items });
    };
    reader.readAsArrayBuffer(file);
    // reset input per permettere di ricaricare lo stesso file
    e.target.value = '';
  };

  const handleSaveLista = async () => {
    if (!uploadPreview) return;
    setUploadSaving(true);
    try {
      const r = await createListaDesiderata(id, {
        nome_file: uploadPreview.nomeFile,
        items: uploadPreview.items,
      });
      setListe(prev => [r.data, ...prev]);
      setUploadPreview(null);
    } catch {
      alert('Errore nel salvataggio della lista.');
    } finally {
      setUploadSaving(false);
    }
  };

  const handleDeleteLista = async (listaId) => {
    if (!window.confirm('Eliminare questa lista?')) return;
    try {
      await deleteListaDesiderata(id, listaId);
      setListe(prev => prev.filter(l => l.id !== listaId));
    } catch {
      alert('Errore nella cancellazione.');
    }
  };

  const statusBadge = (status) => {
    const map = {
      disponibile:   { bg: 'success',   label: 'Disponibile' },
      ordinabile:    { bg: 'warning',   label: 'Ordinabile' },
      fuori_catalogo:{ bg: 'secondary', label: 'Fuori Catalogo' },
      non_trovato:   { bg: 'danger',    label: 'Non trovato' },
    };
    const s = map[status] || { bg: 'light', label: status };
    return <Badge bg={s.bg}>{s.label}</Badge>;
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = value;

    if (name === 'cap' || name === 'provincia') {
      newValue = value.toUpperCase();
    } else if (['nome', 'cognome', 'denominazione_sociale', 'presso', 'luogo_nascita', 'indirizzo', 'citta'].includes(name)) {
      newValue = titleCase(value);
    }

    setFormData({
      ...formData,
      [name]: type === 'number' ? parseInt(newValue, 10) : newValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation check
    if ((!formData.nome || !formData.cognome) && !formData.denominazione_sociale) {
      alert('È obbligatorio inserire Nome e Cognome oppure la Denominazione Sociale.');
      return;
    }

    try {
      const dataToSubmit = { ...formData };

      // Convert empty strings to null for optional fields
      const optionalFields = [
        'denominazione_sociale', 'presso', 'tipologia',
        'codice_fiscale', 'partita_iva', 'indirizzo', 'numero_civico', 'cap', 'citta',
        'provincia', 'nazione', 'codice_destinatario', 'telefono',
        'cellulare', 'email', 'pec', 'luogo_nascita', 'data_nascita'
      ];

      optionalFields.forEach(field => {
        if (dataToSubmit[field] === '') {
          dataToSubmit[field] = null;
        }
      });

      // Ensure data_nascita is null if empty string
      if (dataToSubmit.data_nascita === '') {
        dataToSubmit.data_nascita = null;
      }

      let response;
      let newClientId = null;

      const returnTo = location.state && location.state.returnTo;

      if (id && id !== 'new') { // Modalità modifica
        response = await updateCliente(id, dataToSubmit);
        alert('Cliente aggiornato con successo!');
        navigate(returnTo || '/clienti');
      } else { // Modalità creazione (id è 'new')
        response = await createCliente(dataToSubmit); // Usa createCliente
        newClientId = response.data.id; // Assumi che l'API restituisca l'oggetto cliente con l'ID
        alert('Cliente creato con successo!');

        if (returnTo) {
          navigate(returnTo, { state: { newClientId: newClientId } });
        } else if (location.state && location.state.fromOrder) {
          navigate('/nuovo/ordine', { state: { newClientId: newClientId } });
        } else {
          navigate('/clienti');
        }
      }
    } catch (error) {
      console.error("Errore nell'aggiornamento del cliente:", error.response || error);
      let errorMsg = 'Si è verificato un errore sconosciuto.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => `${err.loc.join(' -> ')}: ${err.msg}`).join('\n');
        } else {
          errorMsg = detail;
        }
      }
      alert(`Errore nell'aggiornamento del cliente:\n${errorMsg}`);
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Caricamento dati cliente...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate('/clienti')}>Torna alla lista clienti</Button>
      </Container>
    );
  }

  return (
    <Container>
        <Row className="align-items-center mb-3">
            <Col>
                <h2>{id && id !== 'new' ? `Modifica Cliente: ${formData.nome} ${formData.cognome}` : 'Nuovo Cliente'}</h2>
            </Col>
            {!isMobile && (
              <Col xs="auto" className="ms-auto">
                  <Button variant="secondary" onClick={handleCancel} className="me-2">
                      Torna alla Lista
                  </Button>
                  <Button variant="primary" type="submit" form="cliente-edit-form">
                      {id && id !== 'new' ? 'Salva Modifiche' : 'Crea Cliente'}
                  </Button>
              </Col>
            )}
        </Row>
      <Form id="cliente-edit-form" onSubmit={handleSubmit}>
        {/* Dati Anagrafici */}
        <Card className="mb-4">
          <Card.Header>Dati Anagrafici</Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Form.Group as={Col} xs={12} md={6} controlId="formNome">
                <Form.Label>Nome</Form.Label>
                <Form.Control type="text" name="nome" value={formData.nome} onChange={handleChange} placeholder="Nome" />
              </Form.Group>
              <Form.Group as={Col} xs={12} md={6} controlId="formCognome">
                <Form.Label>Cognome</Form.Label>
                <Form.Control type="text" name="cognome" value={formData.cognome} onChange={handleChange} placeholder="Cognome" />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} xs={12} md={12} controlId="formDenominazioneSociale">
                <Form.Label>Denominazione Sociale</Form.Label>
                <Form.Control type="text" name="denominazione_sociale" value={formData.denominazione_sociale} onChange={handleChange} placeholder="Denominazione Sociale" />
              </Form.Group>
            </Row>
            <Form.Group className="mb-3" controlId="formPresso">
              <Form.Label>Presso</Form.Label>
              <Form.Control type="text" name="presso" value={formData.presso} onChange={handleChange} placeholder="Presso" />
            </Form.Group>
            <Row className="mb-3">
              <Form.Group as={Col} xs={12} md={4} controlId="formLuogoNascita">
                <Form.Label>Luogo di Nascita</Form.Label>
                <Form.Control type="text" name="luogo_nascita" value={formData.luogo_nascita} onChange={handleChange} />
              </Form.Group>
              <Form.Group as={Col} xs={12} md={4} controlId="formDataNascita">
                <Form.Label>Data di Nascita</Form.Label>
                <Form.Control type="date" name="data_nascita" value={formData.data_nascita} onChange={handleChange} />
              </Form.Group>
              <Form.Group as={Col} xs={12} md={4} controlId="formOrigine">
                <Form.Label>Origine</Form.Label>
                <Form.Select name="origine" value={formData.origine} onChange={handleChange}>
                  <option value="">Seleziona...</option>
                  <option value="NEGOZIO">NEGOZIO</option>
                  <option value="SITO WEB">SITO WEB</option>
                  <option value="IBS">IBS</option>
                  <option value="RIVERSAMENTI">RIVERSAMENTI</option>
                  <option value="ALTRO">ALTRO</option>
                </Form.Select>
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} xs={12} md={6} controlId="formCodiceFiscale">
                <Form.Label>Codice Fiscale</Form.Label>
                <Form.Control type="text" name="codice_fiscale" value={formData.codice_fiscale} onChange={handleChange} placeholder="11 o 16 caratteri" minLength="11" maxLength="16" />
              </Form.Group>
              <Form.Group as={Col} xs={12} md={6} controlId="formPartitaIva">
                <Form.Label>Partita IVA</Form.Label>
                <Form.Control type="text" name="partita_iva" value={formData.partita_iva} onChange={handleChange} placeholder="11 caratteri" minLength="11" maxLength="11" />
              </Form.Group>
            </Row>
          </Card.Body>
        </Card>

        {/* Indirizzo */}
        <Card className="mb-4">
          <Card.Header>Indirizzo</Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Form.Group as={Col} xs={12} md={9} controlId="formIndirizzo">
                <Form.Label>Indirizzo</Form.Label>
                <Form.Control type="text" name="indirizzo" value={formData.indirizzo} onChange={handleChange} placeholder="Via, Piazza, ecc."/>
              </Form.Group>
              <Form.Group as={Col} xs={12} md={3} controlId="formNumeroCivico">
                <Form.Label>N. Civico</Form.Label>
                <Form.Control type="text" name="numero_civico" value={formData.numero_civico} onChange={handleChange} placeholder="Es. 10B"/>
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} xs={12} md={3} controlId="formCap">
                <Form.Label>CAP</Form.Label>
                <Form.Control type="text" name="cap" value={formData.cap} onChange={handleChange} placeholder="Inserisci CAP" />
              </Form.Group>
              <Form.Group as={Col} xs={12} md={5} controlId="formCitta">
                <Form.Label>Città</Form.Label>
                <Form.Control type="text" name="citta" value={formData.citta} onChange={handleChange} />
              </Form.Group>
              <Form.Group as={Col} xs={6} md={2} controlId="formProvincia">
                <Form.Label>Provincia</Form.Label>
                <Form.Control type="text" name="provincia" value={formData.provincia} onChange={handleChange} minLength="2" maxLength="2" placeholder="XX"/>
              </Form.Group>
              <Form.Group as={Col} xs={6} md={2} controlId="formNazione">
                <Form.Label>Nazione</Form.Label>
                <Form.Control type="text" name="nazione" value={formData.nazione} onChange={handleChange} />
              </Form.Group>
            </Row>
          </Card.Body>
        </Card>

        {/* Contatti */}
        <Card className="mb-4">
          <Card.Header>Contatti</Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Form.Group as={Col} xs={12} md={6} controlId="formEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} placeholder="esempio@dominio.com" />
              </Form.Group>
              <Form.Group as={Col} xs={12} md={6} controlId="formPec">
                <Form.Label>PEC</Form.Label>
                <Form.Control type="email" name="pec" value={formData.pec} onChange={handleChange} placeholder="esempio@pec.it" />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} xs={12} md={6} controlId="formTelefono">
                <Form.Label>Telefono</Form.Label>
                <Form.Control type="tel" name="telefono" value={formData.telefono} onChange={handleChange} placeholder="XXXX / XXXXXXXXX"/>
              </Form.Group>
              <Form.Group as={Col} xs={12} md={6} controlId="formCellulare">
                <Form.Label>Cellulare</Form.Label>
                <Form.Control type="tel" name="cellulare" value={formData.cellulare} onChange={handleChange} placeholder="XXXX / XXXXXXXXX" />
              </Form.Group>
            </Row>
            <Form.Group className="mb-3" controlId="formCodiceDestinatario">
              <Form.Label>Cod. Destinatario (Fatt. Elettronica)</Form.Label>
              <Form.Control type="text" name="codice_destinatario" value={formData.codice_destinatario} onChange={handleChange} minLength="7" maxLength="7" />
            </Form.Group>
          </Card.Body>
        </Card>

        {/* Altri Dati */}
        <Card className="mb-4">
          <Card.Header>Altri Dati</Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Form.Group as={Col} xs={12} md={6} controlId="formPuntiFedelta">
                <Form.Label>Punti Fedeltà</Form.Label>
                <Form.Control type="number" name="punti_fedelta" value={formData.punti_fedelta} onChange={handleChange} />
              </Form.Group>
              <Form.Group as={Col} xs={12} md={6} controlId="formScontoPercentuale">
                <Form.Label>Sconto Percentuale Automatico (%)</Form.Label>
                <Form.Control 
                  type="number" 
                  name="sconto_percentuale" 
                  value={formData.sconto_percentuale} 
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="0.0"
                />
                <Form.Text className="text-muted">
                  Sconto applicato automaticamente agli ordini del cliente (0-100%)
                </Form.Text>
              </Form.Group>
              <Form.Group as={Col} xs={12} controlId="formContrassegnoBloccato">
                <Form.Check
                  type="switch"
                  label="Contrassegno disabilitato"
                  checked={!!formData.contrassegno_bloccato}
                  onChange={(e) => setFormData(prev => ({ ...prev, contrassegno_bloccato: e.target.checked }))}
                />
                <Form.Text className="text-muted">
                  Si attiva da solo quando un ordine passa a "Non Ritirato - Contrassegno": il cliente non vede più il pagamento in contrassegno nel checkout. Disattivalo per ripristinarglielo.
                </Form.Text>
              </Form.Group>
            </Row>
          </Card.Body>
        </Card>

        {id && id !== 'new' && <StoricoRimborsiCliente clienteId={id} />}

        {/* Liste Desiderate — solo in modalità modifica */}
        {id && id !== 'new' && (
          <Card className="mb-4">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <span>Liste Desiderate</span>
              <Button size="sm" variant="outline-primary" onClick={() => fileInputRef.current?.click()}>
                Carica XLSX
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleXlsxUpload}
              />
            </Card.Header>
            <Card.Body>
              {/* Anteprima file caricato, prima del salvataggio */}
              {uploadPreview && (
                <div className="mb-4">
                  <p className="mb-2">
                    <strong>{uploadPreview.nomeFile}</strong> — {uploadPreview.items.length} righe
                  </p>
                  <Table size="sm" bordered responsive className="mb-2">
                    <thead>
                      <tr>
                        <th>EAN</th>
                        <th>Titolo (dal file)</th>
                        <th>Qt desiderata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadPreview.items.slice(0, 50).map((r, i) => (
                        <tr key={i}>
                          <td>{r.ean || '—'}</td>
                          <td>{r.titolo_cliente || '—'}</td>
                          <td>{r.qt_desiderata}</td>
                        </tr>
                      ))}
                      {uploadPreview.items.length > 50 && (
                        <tr><td colSpan={3} className="text-muted text-center">… e altri {uploadPreview.items.length - 50}</td></tr>
                      )}
                    </tbody>
                  </Table>
                  <Button variant="primary" size="sm" onClick={handleSaveLista} disabled={uploadSaving} className="me-2">
                    {uploadSaving ? <Spinner size="sm" animation="border" /> : 'Salva lista'}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setUploadPreview(null)}>
                    Annulla
                  </Button>
                </div>
              )}

              {/* Liste già salvate */}
              {listeLoading ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
              ) : liste.length === 0 ? (
                <p className="text-muted mb-0">Nessuna lista caricata.</p>
              ) : (
                liste.map(lista => (
                  <div key={lista.id} className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div>
                        <strong>{lista.nome_file || `Lista #${lista.id}`}</strong>
                        <span className="text-muted ms-2" style={{ fontSize: '0.85em' }}>
                          {new Date(lista.data_caricamento).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDeleteLista(lista.id)}
                      >
                        Elimina
                      </Button>
                    </div>
                    <Table size="sm" bordered responsive>
                      <thead>
                        <tr>
                          <th>EAN</th>
                          <th>Titolo (dal file)</th>
                          <th>Qt</th>
                          <th>Trovato nel catalogo</th>
                          <th>Prezzo</th>
                          <th>Qt in stock</th>
                          <th>Stato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lista.items.map(item => (
                          <tr key={item.id}>
                            <td>{item.ean || '—'}</td>
                            <td>{item.titolo_cliente || '—'}</td>
                            <td>{item.qt_desiderata}</td>
                            <td>
                              {item.film_id
                                ? <a href={`/film/${item.film_id}`} target="_blank" rel="noreferrer">{item.film_titolo}</a>
                                : <span className="text-muted">—</span>
                              }
                            </td>
                            <td>{item.film_prezzo != null ? `€ ${Number(item.film_prezzo).toFixed(2)}` : '—'}</td>
                            <td>{item.film_quantita != null ? item.film_quantita : '—'}</td>
                            <td>{statusBadge(item.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        )}

        {/* Mobile Action Buttons */}
        {isMobile && (
          <div className="d-grid gap-2 mb-4">
            <Button variant="primary" type="submit">
              {id && id !== 'new' ? 'Salva Modifiche' : 'Crea Cliente'}
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              Torna alla Lista
            </Button>
          </div>
        )}


      </Form>
    </Container>
  );
};

export default ClientiEdit;
